// Pure, Angular-free, pouch-free helpers for classifying a failed food-doc read.
// Kept out of food-db.service.ts so they can be unit-tested under node (that
// service transitively imports pouchdb-browser, which references `self` and
// cannot load in the test runner — same reason database-wrapper's pure logic
// lives in sync-failure-classification.ts / database-wrapper-passthrough.ts).
import { isCouchUsersDocConflictError, isTransientFetchError } from './error-patterns';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { isDatabaseUnavailable } from './database-unavailable-error';
import { describeError } from '@cf-shared/error-describe';

/**
 * What a caller wants done when a food doc could not be READ at all — as opposed
 * to being read successfully and turning out not to exist.
 *
 * Every caller of `getFoodByIdAsync` must pass one of these; there is deliberately
 * no default. Both answers are legitimate depending on the caller, so there is no
 * safe value to fall back to, and an option that can be left out is one that gets
 * left out by accident (df-2719c17f4588). The build now asks the question instead
 * of a comment asking it.
 */
export enum FoodReadFailurePolicy {
  /** Raise `FoodReadFailedError` when the read could not complete, so the caller can
   * tell "could not read" apart from "does not exist". A `null` return then means
   * ONLY a genuine 404. Pick this wherever the difference reaches the user — telling
   * somebody their food is gone when their connection dropped for a second sends them
   * off to re-create a food they still have. */
  Throw = 'throw',
  /** Answer `null` for a failed read, exactly as for a food that does not exist. The
   * long-standing behaviour, and a legitimate one where the caller genuinely treats
   * "could not get it" as a normal outcome — background caching, a lookup that falls
   * through to a search, a display path that self-heals on the next reload. Read it as
   * a claim that this caller does not care which of the two happened. */
  TreatAsMissing = 'treat-as-missing',
}

/**
 * Thrown by `getFoodByIdAsync` (only when the caller passes
 * `onReadFailure: FoodReadFailurePolicy.Throw`) when a food doc could NOT be read
 * because of a transient condition — a network blip, an iOS WKWebView "Load
 * failed", the local database being mid-recovery / shutting down, or an otherwise
 * unexpected read error. This is deliberately distinct from a `null` return, which
 * means "the food genuinely does not exist (404)". Callers that would otherwise
 * treat a read failure as "food is missing" catch this and keep the existing
 * reference intact (the read self-heals once the DB / network recovers).
 */
export class FoodReadFailedError extends Error {
  readonly foodId: string;
  readonly originalError: unknown;

  constructor(foodId: string, originalError: unknown) {
    const message = describeError(originalError);
    super(`Could not read food ${foodId}: ${message}`);
    this.name = 'FoodReadFailedError';
    this.foodId = foodId;
    this.originalError = originalError;
  }
}

/** How a failed food-doc read should be interpreted, after the in-helper retry
 * (if any) has been exhausted. */
export enum FoodReadOutcome {
  /** Genuine 404 — the food really is gone. Safe to treat as "does not exist". */
  NotFound = 'not-found',
  /** Network blip / iOS "Load failed" — worth one retry; on the first pass this
   * also drives the single retry inside getFoodByIdAsync. */
  Transient = 'transient',
  /** Local DB shutting down (logout) or mid-recovery — could not read, not missing. */
  DbUnavailable = 'db-unavailable',
  /** Anything else — surface for telemetry; still "could not read", not missing. */
  Unexpected = 'unexpected',
}

/** Classify a food-doc read error. Order matters and mirrors the historical
 * branch order: transient is checked first (it drives the retry), then a genuine
 * not-found, then the DB-unavailable shutdown case, then everything else. */
export function classifyFoodReadError(error: unknown): FoodReadOutcome {
  if (isTransientFetchError(error)) return FoodReadOutcome.Transient;
  // CouchDB refusing a brand-new account for the few seconds its login record holds two
  // conflicting versions (df-805d12d77cff). During the first sync a food read is served
  // from the remote database, so it really does go out over the network and really can
  // meet this. It is transient by construction — the server repairs the record and the
  // next attempt succeeds — so the retry above is the right answer, and it is what makes
  // the food actually arrive instead of the read quietly coming back empty.
  if (isCouchUsersDocConflictError(error)) return FoodReadOutcome.Transient;
  if (isPouchNotFound(error)) return FoodReadOutcome.NotFound;
  // match the TYPE, not the message text. The wording lives in
  // `DatabaseUnavailableReason` and reads like free-form prose, so a substring
  // match here would break silently the first time someone reworded it.
  if (isDatabaseUnavailable(error)) return FoodReadOutcome.DbUnavailable;
  return FoodReadOutcome.Unexpected;
}

/** What the app looked like at one moment of a failed food read. */
export interface FoodReadConnectionMoment {
  /** `document.hidden`: the app was in the background. */
  documentHidden: boolean;
  /** `OnlineStateService.isLikelyOnline()`: the app believed it had a connection. */
  online: boolean;
}

/** Which report a food read that still failed after its retry gets. */
export enum FoodReadFailureReport {
  /** The app was open and online the whole time, so a read that keeps failing is a
   * real "connection keeps failing" signal that a person may need to look at. */
  Problem = 'problem',
  /** The app was in the background or offline when one of the reads failed. A fetch
   * failing then is the ordinary way this offline-first app loses its connection,
   * and nothing is left for a person to act on (df-a4572fc2f544). */
  TransientInfo = 'transient-info',
}

/** Report name for `FoodReadFailureReport.Problem`. */
export const FOOD_READ_NETWORK_ERROR_PROBLEM = 'getFoodByIdAsync network error';
/** Report name for `FoodReadFailureReport.TransientInfo`. It carries the
 * `(transient network)` suffix that the other deferred-read reports use for the same
 * kind of failure (see session-health.ts). */
export const FOOD_READ_NETWORK_ERROR_TRANSIENT = 'getFoodByIdAsync network error (transient network)';

/** Pick the report for a food read that failed, was retried once, and failed again.
 * Both moments are looked at, because the app can come back to the foreground (or
 * back online) during the wait before the retry, and the first failure still came
 * from the dropped connection. */
export function chooseFoodReadFailureReport(
  firstFailure: FoodReadConnectionMoment,
  retryFailure: FoodReadConnectionMoment,
): FoodReadFailureReport {
  // if the app was backgrounded or offline at either read, the connection dropped
  // for an ordinary reason: record it, do not file a problem
  for (const moment of [firstFailure, retryFailure]) {
    if (moment.documentHidden || !moment.online) return FoodReadFailureReport.TransientInfo;
  }
  return FoodReadFailureReport.Problem;
}
